import { useCallback, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import * as AuthSession from "expo-auth-session";
import { useAuthStore } from "@src/features/auth/auth-store";
import { authSessionStorage, SESSION_KEYS } from "@src/features/auth/utils";
import { useAuthConfig } from "@src/features/auth/hooks";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import * as Crypto from "expo-crypto";
import { triggerAccountLink } from "./account-link";

/**
 * Hook to initiate Auth0 login flow.
 * Can be used from anywhere in the app (e.g., in a modal).
 */
export const useAuth0Login = () => {
  const { track } = useAmplitude();
  const handleTokenExchange = useAuthStore((state) => state.handleTokenExchange);
  const [isRequestReady, setIsRequestReady] = useState(false);
  const isExchangingCodeRef = useRef(false);

  const authConfig = useAuthConfig();

  // Generate CSRF token
  const [csrfToken] = useState(() => {
    const token = Crypto.randomUUID();
    authSessionStorage.set(SESSION_KEYS.CSRF_TOKEN, token);
    return token;
  });

  // Generate nonce
  const [nonce] = useState(() => {
    const token = Crypto.randomUUID();
    authSessionStorage.set(SESSION_KEYS.NONCE, token);
    return token;
  });

  // Auth request setup with PKCE
  // Note: Fallbacks are for type satisfaction only - when discovery is null, request is null
  const [request, , promptAsync] = AuthSession.useAuthRequest(
    {
      clientId: authConfig?.clientId ?? "",
      scopes: ["openid", "profile", "email", "offline_access"],
      redirectUri: authConfig?.redirectUri ?? "",
      responseType: AuthSession.ResponseType.Code,
      usePKCE: true,
      codeChallengeMethod: AuthSession.CodeChallengeMethod.S256,
      state: JSON.stringify({ csrfToken }),
      extraParams: {
        nonce,
        ...(authConfig?.audience ? { audience: authConfig.audience } : {}),
        ...(authConfig?.authConnection ? { connection: authConfig.authConnection } : {}),
        access_type: "offline",
      },
    },
    authConfig?.discovery ?? null,
  );

  // Store code verifier for web redirect flow
  useEffect(() => {
    if (request?.codeVerifier) {
      authSessionStorage.set(SESSION_KEYS.CODE_VERIFIER, request.codeVerifier);
      setIsRequestReady(true);
    }
  }, [request?.codeVerifier]);

  // Exchange authorization code for tokens
  const exchangeCode = useCallback(
    async (code: string, codeVerifier: string) => {
      if (!authConfig) {
        throw new Error("Auth config not loaded");
      }
      try {
        const tokenResponse = await AuthSession.exchangeCodeAsync(
          {
            clientId: authConfig.clientId,
            code,
            redirectUri: authConfig.redirectUri,
            extraParams: { code_verifier: codeVerifier },
          },
          authConfig.discovery,
        );

        authSessionStorage.clearAuthData();
        await handleTokenExchange(tokenResponse);
        await triggerAccountLink(tokenResponse);

        // Track successful Auth0 login completion
        track(ANALYTICS_EVENTS.AUTH0_LOGIN_COMPLETED);
      } catch (error) {
        // Track Auth0 login failure
        track(ANALYTICS_EVENTS.AUTH0_LOGIN_FAILED, {
          error_type: error instanceof Error ? error.message : "unknown",
        });
        throw error;
      } finally {
        isExchangingCodeRef.current = false;
      }
    },
    [authConfig, handleTokenExchange, track],
  );

  // Handle web callback (URL contains auth params after redirect)
  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    if (isExchangingCodeRef.current) return;

    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const stateParam = url.searchParams.get("state");

    if (!code || !stateParam) return;

    // Clean URL immediately to prevent re-processing
    window.history.replaceState({}, document.title, window.location.pathname);
    isExchangingCodeRef.current = true;

    (async () => {
      try {
        // Validate CSRF token
        const stateData = JSON.parse(stateParam) as { csrfToken?: string };
        const storedCsrf = authSessionStorage.get(SESSION_KEYS.CSRF_TOKEN);
        if (stateData.csrfToken !== storedCsrf) {
          throw new Error("CSRF token mismatch");
        }

        const codeVerifier = authSessionStorage.get(SESSION_KEYS.CODE_VERIFIER);
        if (!codeVerifier) {
          throw new Error("Missing code verifier");
        }

        await exchangeCode(code, codeVerifier);
      } catch (error) {
        console.error("[useAuth0Login] Web callback error:", error);
        isExchangingCodeRef.current = false;
      }
    })();
  }, [exchangeCode]);

  // Note: the native/popup auth result is handled inline in `initiateAuth` below,
  // directly from the `promptAsync` return value. We deliberately do NOT react to the
  // `result` state here: doing so could trigger a second `exchangeCode` for the same
  // auth (the code comment in `initiateAuth` notes the `result` state can be lost or
  // replayed on remount), and each authorization code may only be exchanged once.

  // Initiate auth flow
  const initiateAuth = useCallback(async () => {
    if (!request) {
      throw new Error("Auth request not ready");
    }

    // Track Auth0 login start
    track(ANALYTICS_EVENTS.AUTH0_LOGIN_STARTED);

    try {
      const promptResult = await promptAsync();

      // Handle the result directly from promptAsync instead of waiting for the result state
      // This fixes the issue where the component may have re-mounted during the auth flow
      // and the result state is lost
      if (promptResult?.type === "success" && "params" in promptResult && promptResult.params.code) {
        // Validate CSRF token
        const returnedState = promptResult.params.state;
        if (returnedState) {
          const stateData = JSON.parse(returnedState) as { csrfToken?: string };
          if (stateData.csrfToken !== csrfToken) {
            throw new Error("CSRF token mismatch");
          }
        }

        const codeVerifier = request?.codeVerifier || authSessionStorage.get(SESSION_KEYS.CODE_VERIFIER);
        if (!codeVerifier) {
          throw new Error("Missing code verifier");
        }

        await exchangeCode(promptResult.params.code, codeVerifier);
      } else if (promptResult?.type === "error") {
        const errorMsg = "params" in promptResult ? promptResult.params?.error_description : "Authentication failed";
        throw new Error(errorMsg || "Authentication failed");
      } else if (promptResult?.type === "cancel" || promptResult?.type === "dismiss") {
        // Don't throw - user intentionally cancelled
      }
    } catch (error) {
      track(ANALYTICS_EVENTS.AUTH0_LOGIN_FAILED, {
        error_type: "initiation_failed",
      });
      throw error;
    }
  }, [request, promptAsync, track, csrfToken, exchangeCode]);

  const isReady = isRequestReady;

  return {
    initiateAuth,
    isReady,
  };
};
